import React, { useState } from 'react';
import { 
  Volume2, 
  DollarSign, 
  Clock, 
  TrendingUp, 
  TrendingDown,
  Search,
  Headphones,
} from 'lucide-react';
import { 
  LineChart, 
  Line, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  AreaChart,
  Area
} from 'recharts';

const TTSStats = ({ stats, loading }) => {
  const [selectedProvider, setSelectedProvider] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('requests');

  const COLORS = ['#10B981', '#3B82F6', '#8B5CF6', '#F59E0B', '#EF4444'];

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="bg-white rounded-xl border border-gray-200 p-6 animate-pulse">
              <div className="h-4 bg-gray-200 rounded w-3/4 mb-4"></div>
              <div className="h-8 bg-gray-200 rounded w-1/2 mb-2"></div>
              <div className="h-3 bg-gray-200 rounded w-full"></div>
            </div>
          ))}
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-white rounded-xl border border-gray-200 p-6 animate-pulse h-80"></div>
          <div className="bg-white rounded-xl border border-gray-200 p-6 animate-pulse h-80"></div>
        </div>
      </div>
    );
  }

  if (!stats) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">Нет данных для отображения</p>
      </div>
    );
  }

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('ru-RU', {
      style: 'currency',
      currency: 'RUB',
      minimumFractionDigits: 2,
    }).format(value);
  };

  const formatNumber = (value) => {
    return new Intl.NumberFormat('ru-RU').format(value);
  };

  const formatDuration = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = Math.floor(seconds % 60);
    
    if (hours > 0) {
      return `${hours}ч ${minutes}м ${secs}с`;
    } else if (minutes > 0) {
      return `${minutes}м ${secs}с`;
    } else {
      return `${secs}с`;
    }
  };

  // Filter and sort providers
  const providers = stats.providers || [];
  const filteredProviders = providers
    .filter(provider => 
      selectedProvider === 'all' || provider.name === selectedProvider
    )
    .filter(provider =>
      provider.name.toLowerCase().includes(searchTerm.toLowerCase())
    )
    .sort((a, b) => {
      switch (sortBy) {
        case 'requests':
          return b.requests - a.requests;
        case 'cost':
          return b.cost - a.cost;
        case 'responseTime':
          return a.avgResponseTime - b.avgResponseTime;
        case 'duration':
          return b.totalDuration - a.totalDuration;
        default:
          return 0;
      }
    });

  const usageTrendData = stats.usageTrend || [];
  const voiceDistributionData = stats.voiceDistribution || [];
  const durationTrendData = stats.durationTrend || [];
  const qualityMetricsData = stats.qualityMetrics || [];

  const totalRequests = stats.totalRequests || 0;
  const totalCost = stats.totalCost || 0;
  const totalDuration = stats.totalDuration || 0;
  const avgQualityScore = stats.avgQualityScore || 0;

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <div className="flex items-start justify-between mb-4">
            <div className="p-3 bg-green-100 rounded-lg">
              <Volume2 className="w-6 h-6 text-green-600" />
            </div>
          </div>
          <h3 className="text-gray-600 text-sm font-medium mb-1">Всего запросов</h3>
          <p className="text-3xl font-bold text-gray-900 mb-2">{formatNumber(totalRequests)}</p>
          <div className="flex items-center gap-1 text-sm">
            {stats.requestsTrend >= 0 ? (
              <TrendingUp className="w-4 h-4 text-green-500" />
            ) : (
              <TrendingDown className="w-4 h-4 text-red-500" />
            )}
            <span className={stats.requestsTrend >= 0 ? 'text-green-600' : 'text-red-600'}>
              {Math.abs(stats.requestsTrend)}% за период
            </span>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <div className="flex items-start justify-between mb-4">
            <div className="p-3 bg-blue-100 rounded-lg">
              <DollarSign className="w-6 h-6 text-blue-600" />
            </div>
          </div>
          <h3 className="text-gray-600 text-sm font-medium mb-1">Общие затраты</h3>
          <p className="text-3xl font-bold text-gray-900 mb-2">{formatCurrency(totalCost)}</p>
          <div className="flex items-center gap-1 text-sm">
            {stats.costTrend >= 0 ? (
              <TrendingUp className="w-4 h-4 text-red-500" />
            ) : (
              <TrendingDown className="w-4 h-4 text-green-500" />
            )}
            <span className={stats.costTrend >= 0 ? 'text-red-600' : 'text-green-600'}>
              {Math.abs(stats.costTrend)}% за период
            </span>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <div className="flex items-start justify-between mb-4">
            <div className="p-3 bg-purple-100 rounded-lg">
              <Clock className="w-6 h-6 text-purple-600" />
            </div>
          </div>
          <h3 className="text-gray-600 text-sm font-medium mb-1">Общая длительность</h3>
          <p className="text-3xl font-bold text-gray-900 mb-2">{formatDuration(totalDuration)}</p>
          <div className="flex items-center gap-1 text-sm">
            {stats.durationTrend >= 0 ? (
              <TrendingUp className="w-4 h-4 text-green-500" />
            ) : (
              <TrendingDown className="w-4 h-4 text-red-500" />
            )}
            <span className={stats.durationTrend >= 0 ? 'text-green-600' : 'text-red-600'}>
              {Math.abs(stats.durationTrend)}% за период
            </span>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <div className="flex items-start justify-between mb-4">
            <div className="p-3 bg-orange-100 rounded-lg">
              <Headphones className="w-6 h-6 text-orange-600" />
            </div>
          </div>
          <h3 className="text-gray-600 text-sm font-medium mb-1">Качество звука</h3>
          <p className="text-3xl font-bold text-gray-900 mb-2">{avgQualityScore}/10</p>
          <div className="flex items-center gap-1 text-sm">
            {stats.qualityTrend >= 0 ? (
              <TrendingUp className="w-4 h-4 text-green-500" />
            ) : (
              <TrendingDown className="w-4 h-4 text-red-500" />
            )}
            <span className={stats.qualityTrend >= 0 ? 'text-green-600' : 'text-red-600'}>
              {Math.abs(stats.qualityTrend)}% за период
            </span>
          </div>
        </div>
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Usage Trend Chart */}
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Динамика использования</h3>
          <ResponsiveContainer width="100%" height={300}>
            <AreaChart data={usageTrendData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis 
                dataKey="date" 
                tick={{ fontSize: 12 }}
                tickFormatter={(value) => new Date(value).toLocaleDateString('ru-RU', { day: '2-digit', month: 'short' })}
              />
              <YAxis tick={{ fontSize: 12 }} />
              <Tooltip 
                labelFormatter={(value) => new Date(value).toLocaleDateString('ru-RU')}
                formatter={(value) => [formatNumber(value), 'Запросов']}
              />
              <Area 
                type="monotone" 
                dataKey="requests" 
                stroke="#10B981" 
                fill="#10B981"
                fillOpacity={0.3}
                strokeWidth={2}
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        {/* Voice Distribution Pie Chart */}
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Распределение по голосам</h3>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={voiceDistributionData}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                outerRadius={80}
                fill="#8884d8"
                dataKey="value"
              >
                {voiceDistributionData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip formatter={(value) => [formatNumber(value), 'Запросов']} />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Duration Trend Chart */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Динамика длительности аудио</h3>
        <ResponsiveContainer width="100%" height={300}>
          <LineChart data={durationTrendData}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis 
              dataKey="date" 
              tick={{ fontSize: 12 }}
              tickFormatter={(value) => new Date(value).toLocaleDateString('ru-RU', { day: '2-digit', month: 'short' })}
            />
            <YAxis 
              tick={{ fontSize: 12 }}
              tickFormatter={(value) => formatDuration(value)}
            />
            <Tooltip 
              labelFormatter={(value) => new Date(value).toLocaleDateString('ru-RU')}
              formatter={(value) => [formatDuration(value), 'Длительность']}
            />
            <Line 
              type="monotone" 
              dataKey="duration" 
              stroke="#8B5CF6" 
              strokeWidth={2}
              dot={{ r: 4 }}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>

      {/* Quality Metrics Chart */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Метрики качества</h3>
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={qualityMetricsData}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis 
              dataKey="provider" 
              tick={{ fontSize: 12 }}
            />
            <YAxis tick={{ fontSize: 12 }} domain={[0, 10]} />
            <Tooltip 
              formatter={(value) => [`${value}/10`, 'Оценка качества']}
            />
            <Bar dataKey="quality" fill="#F59E0B" />
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Providers Table */}
      <div className="bg-white rounded-xl border border-gray-200">
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-gray-900">Провайдеры TTS</h3>
            <div className="flex items-center gap-3">
              {/* Search */}
              <div className="relative">
                <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
                <input
                  type="text"
                  placeholder="Поиск провайдера..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              {/* Provider Filter */}
              <select
                value={selectedProvider}
                onChange={(e) => setSelectedProvider(e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="all">Все провайдеры</option>
                {providers.map(provider => (
                  <option key={provider.name} value={provider.name}>
                    {provider.name}
                  </option>
                ))}
              </select>

              {/* Sort */}
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="requests">По запросам</option>
                <option value="cost">По затратам</option>
                <option value="responseTime">По времени ответа</option>
                <option value="duration">По длительности</option>
              </select>
            </div>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Провайдер
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Голос
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Запросов
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Затраты
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Время ответа
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Общая длительность
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Качество
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {filteredProviders.map((provider, index) => (
                <tr key={index} className="hover:bg-gray-50">
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-2">
                      <Volume2 className="w-5 h-5 text-green-600" />
                      <span className="font-medium text-gray-900">{provider.name}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-gray-600">{provider.voice}</td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-gray-900">{formatNumber(provider.requests)}</span>
                      {provider.requestsTrend >= 0 ? (
                        <TrendingUp className="w-4 h-4 text-green-500" />
                      ) : (
                        <TrendingDown className="w-4 h-4 text-red-500" />
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-gray-900">{formatCurrency(provider.cost)}</span>
                      {provider.costTrend >= 0 ? (
                        <TrendingUp className="w-4 h-4 text-red-500" />
                      ) : (
                        <TrendingDown className="w-4 h-4 text-green-500" />
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-gray-900">{provider.avgResponseTime}мс</span>
                      {provider.responseTimeTrend <= 0 ? (
                        <TrendingDown className="w-4 h-4 text-green-500" />
                      ) : (
                        <TrendingUp className="w-4 h-4 text-red-500" />
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-gray-900">{formatDuration(provider.totalDuration)}</span>
                      {provider.durationTrend >= 0 ? (
                        <TrendingUp className="w-4 h-4 text-green-500" />
                      ) : (
                        <TrendingDown className="w-4 h-4 text-red-500" />
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-gray-900">{provider.qualityScore}/10</span>
                      {provider.qualityTrend >= 0 ? (
                        <TrendingUp className="w-4 h-4 text-green-500" />
                      ) : (
                        <TrendingDown className="w-4 h-4 text-red-500" />
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {filteredProviders.length === 0 && (
            <div className="text-center py-12 text-gray-500">
              Нет данных для отображения
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TTSStats;
